import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
// import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heading, Text } from '@/components/ui/typography';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
// import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
// import Recorder from "recorder-js";
import requestQueue from "../Questions/RequestQueue";
import axios from "axios";
// import EasySpeech from 'easy-speech';
import screenRecordingManager from "../Questions/ScreenRecordingManager";
import { isDesktopApp, startDesktopProctoring } from "../../utils/desktopBridge.js";
import { ensureEntireScreenSelected, ENTIRE_SCREEN_REQUIRED_MESSAGE } from "../../utils/screenShare";
import { MediaDebugInfo } from "./MediaDebugInfo.jsx";

// Import new step components
import SetupProgress from "./SetupProgress";
import GuidelinesStep from "./GuidelinesStep";
import SystemCheckStep from "./SystemCheckStep";
import ReadyStep from "./ReadyStep";

function TestingSection() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // New stepper state
  const [currentStep, setCurrentStep] = useState(1); // 1=guidelines, 2=system, 3=ready
  const [consentGiven, setConsentGiven] = useState(false);
  const [autoTestsRunning, setAutoTestsRunning] = useState(false);
  const [isStartingInterview, setIsStartingInterview] = useState(false);
  
  // Hardware test states
  const [micStatus, setMicStatus] = useState("Not Tested");
  const [micMessage, setMicMessage] = useState("");
  const [speakerStatus, setSpeakerStatus] = useState("Not Tested");
  const [cameraStatus, setCameraStatus] = useState("Not Tested");
  const [screenShareStatus, setScreenShareStatus] = useState("Not Tested");
  
  // Refs
  const audioContextRef = useRef(null);
  const recorderRef = useRef(null);
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null); // Store camera stream to re-attach on navigation
  
  // Test data states
  const [questions, setQuestions] = useState([]);
  const [testType, setTestType] = useState(null);
  const [resumeQuestions, setResumeQuestions] = useState([]);
  const [backendLanguage, setBackendLanguage] = useState(null);
  const [isVeryHard, setIsVeryHard] = useState(false);
  const [codingQuestions, setCodingQuestions] = useState([]);
  const [durationMinutes, setDurationMinutes] = useState(null);
  
  // Network monitoring states
  const [networkSpeed, setNetworkSpeed] = useState(null);
  const [networkStatus, setNetworkStatus] = useState("checking");
  
  // Screen recording state and refs
  const screenStreamRef = useRef(null);
  const screenRecorderRef = useRef(null);
  const screenChunksRef = useRef([]);
  const micStreamRef = useRef(null);
  const [isScreenRecording, setIsScreenRecording] = useState(false);
  const [screenSharingActive, setScreenSharingActive] = useState(false);
  const [screenShareError, setScreenShareError] = useState("");
  const [showScreenShareStoppedPopup, setShowScreenShareStoppedPopup] = useState(false);
  const [showScreenShareErrorPopup, setShowScreenShareErrorPopup] = useState(false);
  const speechTokenRef = useRef(null);
  const currentAudioRef = useRef(null);
  const currentAudioResolveRef = useRef(null);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);

  // Desktop proctoring warning state
  const [proctoringWarnings, setProctoringWarnings] = useState([]);
  
  const searchParams = new URLSearchParams(location.search);
  const test_id = searchParams.get("test_id");

  const IS_DESKTOP = isDesktopApp();

  const [questionsCount, setQuestionsCount] = useState('-');

  // Initialize desktop screen share status
  useEffect(() => {
    if (IS_DESKTOP) {
      setScreenShareStatus("Tested");
      setScreenSharingActive(true);
      setIsScreenRecording(true);
      console.log("Desktop mode detected: screen recording assumed active");
    }
  }, [IS_DESKTOP]);

  // Fetch questions on mount
  useEffect(() => {
    fetchQuestions();
  }, []);

  // Periodically check network speed
  useEffect(() => {
    checkNetworkSpeed();
    const intervalId = setInterval(checkNetworkSpeed, 5000);
    return () => clearInterval(intervalId);
  }, []);

  // Track if tests have been started manually
  const [testsStarted, setTestsStarted] = useState(false);

  // Re-attach camera stream when returning to step 2
  useEffect(() => {
    if (currentStep === 2 && testsStarted && cameraStreamRef.current && videoRef.current) {
      // Re-attach the camera stream if it exists
      if (videoRef.current.srcObject !== cameraStreamRef.current) {
        console.log("[CAM] Re-attaching camera stream on step navigation");
        videoRef.current.srcObject = cameraStreamRef.current;
        videoRef.current.play().catch(err => {
          console.warn("[CAM] Could not auto-play video:", err);
        });
      }
    }
  }, [currentStep, testsStarted]);

  // Fullscreen management
  useEffect(() => {
    if (IS_DESKTOP) return;

    const handleFullscreenExit = () => {
      if (!document.fullscreenElement && currentStep >= 2) {
        enterFullscreen();
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenExit);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenExit);
  }, [currentStep, IS_DESKTOP]);

  // ==================== Helper Functions ====================

  const enterFullscreen = async () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen();
      } else if (el.mozRequestFullScreen) {
        await el.mozRequestFullScreen();
      } else if (el.msRequestFullscreen) {
        await el.msRequestFullscreen();
      }
      console.log("[Fullscreen] Entered fullscreen successfully");
    } catch (error) {
      console.warn("[Fullscreen] Could not enter fullscreen:", error.message);
    }
  };

  const checkNetworkSpeed = () => {
    const imageAddr = "https://upload.wikimedia.org/wikipedia/commons/2/2d/Snake_River_%285mb%29.jpg" + "?n=" + Math.random();
    const downloadSize = 4995374;
    const startTime = new Date().getTime();
    
    const timeout = setTimeout(() => {
      setNetworkStatus("poor");
    }, 5000);

    const download = new Image();
    download.onload = function () {
      clearTimeout(timeout);
      const endTime = new Date().getTime();
      const duration = (endTime - startTime) / 1000;
      const bitsLoaded = downloadSize * 8;
      const speedBps = (bitsLoaded / duration).toFixed(2);
      const speedKbps = (speedBps / 1024).toFixed(2);
      const speedMbps = (speedKbps / 1024).toFixed(2);
      
      setNetworkSpeed(speedMbps);
      
      if (speedMbps < 1) {
        setNetworkStatus("poor");
      } else if (speedMbps < 3) {
        setNetworkStatus("fair");
      } else {
        setNetworkStatus("good");
      }
    };
    
    download.onerror = function () {
      clearTimeout(timeout);
      setNetworkStatus("poor");
    };
    
    download.src = imageAddr;
  };

  const fetchQuestions = async () => {
    try {
      const response = await fetch(`/api/get_questions?test_id=${test_id}`);
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.question_data);
        setResumeQuestions(data.resume_questions);
        setTestType(data.test_type);
        setBackendLanguage(data.language);
        setIsVeryHard(data?.is_very_hard);
        setCodingQuestions(data?.coding_questions || []);
        setDurationMinutes(data?.duration_minutes || null);

        setQuestionsLoaded(true);
        if (data.total_questions) {
          setQuestionsCount(data.total_questions);
        } else {
          // fallback (should rarely happen)
          setQuestionsCount(Math.max((data.question_data?.length || 0), 1));
        }
      } else {
        console.error("Failed to fetch questions");
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  };

  // ==================== System Check Functions ====================

  const runAllSystemChecks = async () => {
    setAutoTestsRunning(true);
    setTestsStarted(true);
    console.log("[AUTO-TEST] Starting all system checks...");

    try {
      // Run camera, mic, and speaker tests in parallel
      await Promise.all([
        handleCameraTest(),
        handleMicTest(),
        handleSpeakerTest(),
      ]);

      // Screen share test needs user interaction - do this before fullscreen
      // because the screen share dialog will exit fullscreen anyway
      if (!IS_DESKTOP) {
        await handleScreenShareTest();
        
        // Enter fullscreen AFTER screen share is established
        // This way fullscreen won't be interrupted by the screen share dialog
        await enterFullscreen();
      }
    } catch (error) {
      console.error("[AUTO-TEST] Error during system checks:", error);
    } finally {
      setAutoTestsRunning(false);
      console.log("[AUTO-TEST] System checks completed");
    }
  };

  const handleCameraTest = async () => {
    setCameraStatus("Testing");
    console.log("[CAM] Camera test started");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      console.log("[CAM] Got camera stream:", stream.id);
      
      // Store the stream for re-attachment on navigation
      cameraStreamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        await new Promise((resolve, reject) => {
          videoRef.current.onloadedmetadata = () => {
            console.log("[CAM] Video metadata loaded");
            videoRef.current.play()
              .then(() => {
                console.log("[CAM] Video playing");
                resolve();
              })
              .catch(reject);
          };
          
          setTimeout(() => reject(new Error("Camera timeout")), 5000);
        });
        
        setCameraStatus("Tested");
        console.log("[CAM] Camera test successful");
        
      } else {
        throw new Error("Video element not available");
      }
    } catch (error) {
      console.error("[CAM] Camera test failed:", error);
      setCameraStatus("Error");
    }
  };

  const handleMicTest = async () => {
    setMicStatus("Testing");
    setMicMessage("Testing microphone...");
    console.log("[MIC] Mic test started");

    let stream = null;
    let audioContext = null;
    let analyser = null;
    let rafId = null;
    let checkTimeout = null;

    const cleanup = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (checkTimeout) {
        clearTimeout(checkTimeout);
        checkTimeout = null;
      }
      if (stream) {
        stream.getTracks().forEach(t => t.stop());
        stream = null;
      }
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close();
        audioContext = null;
      }
    };

    try {
      stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      console.log("[MIC] Got microphone stream:", stream.id);

      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      const source = audioContext.createMediaStreamSource(stream);
      analyser = audioContext.createAnalyser();

      analyser.fftSize = 2048;
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;
      analyser.smoothingTimeConstant = 0.2;

      source.connect(analyser);

      const buffer = new Uint8Array(analyser.frequencyBinCount);
      let detected = false;
      const startTime = Date.now();
      let sampleCount = 0;

      const check = () => {
        if (detected) return;
        
        analyser.getByteFrequencyData(buffer);
        sampleCount++;

        const sum = buffer.reduce((a, b) => a + b, 0);
        const avg = sum / buffer.length;
        const max = Math.max(...buffer);

        if (max > 5) {
          detected = true;
          console.log("[MIC] Sound detected!");
          setMicStatus("Tested");
          setMicMessage("Microphone is working correctly");
          cleanup();
          return;
        }

        const elapsed = Date.now() - startTime;
        if (elapsed > 3000) {
          if (sampleCount > 10 && avg > 0) {
            setMicStatus("Tested");
            setMicMessage("Microphone is working");
          } else {
            throw new Error("No microphone input detected");
          }
          cleanup();
          return;
        }

        rafId = requestAnimationFrame(check);
      };

      check();
      
      checkTimeout = setTimeout(() => {
        if (!detected) {
          setMicStatus("Tested");
          setMicMessage("Microphone detected");
          cleanup();
        }
      }, 3500);

    } catch (err) {
      console.error("[MIC] Mic test failed:", err);
      setMicStatus("Error");
      setMicMessage(err.message || "Microphone access failed");
      cleanup();
    }
  };

  const handleSpeakerTest = () => {
    return new Promise((resolve) => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
        oscillator.start();
        setSpeakerStatus("Testing");

        setTimeout(() => {
          oscillator.stop();
          audioContext.close();
          setSpeakerStatus("Tested");
          resolve();
        }, 1000);
      } catch (error) {
        console.error("Error generating beep sound:", error);
        setSpeakerStatus("Error");
        resolve();
      }
    });
  };

  // ==================== Screen Recording Functions ====================

  const sendScreenChunkToBackend = () => {
    if (screenChunksRef.current.length === 0) return;
    
    try {
      const blob = new Blob(screenChunksRef.current, { type: 'video/webm' });
      const formData = new FormData();
      formData.append('screen_chunk', blob);
      formData.append('test_id', test_id);
      formData.append('chunk_index', Date.now());
      
      requestQueue.queueScreenChunk(() => 
        axios.post('/api/upload_screen_chunk', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      );
      
      screenChunksRef.current = [];
    } catch (error) {
      console.error("Error sending screen chunk to backend:", error);
    }
  };

  const startScreenRecording = async () => {
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { mediaSource: "screen" },
        audio: true
      });
      ensureEntireScreenSelected(screenStream);

      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const combinedStream = new MediaStream([
        ...screenStream.getVideoTracks(),
        ...micStream.getAudioTracks()
      ]);

      screenStreamRef.current = combinedStream;
      micStreamRef.current = micStream;

      const recorder = new MediaRecorder(combinedStream, {
        mimeType: 'video/webm;codecs=vp9,opus',
        videoBitsPerSecond: 1200000,
        audioBitsPerSecond: 128000
      });

      screenRecorderRef.current = recorder;
      screenChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          screenChunksRef.current.push(event.data);
          sendScreenChunkToBackend();
        }
      };

      recorder.onstop = async () => {
        if (screenChunksRef.current.length > 0) {
          await sendScreenChunkToBackend();
        }
      };

      recorder.start(10000);
      setIsScreenRecording(true);
      setScreenSharingActive(true);
      setScreenShareError("");

      const vtrack = screenStream.getVideoTracks()[0];
      if (vtrack) {
        vtrack.onended = () => {
          if (screenRecorderRef.current && screenRecorderRef.current.state === 'recording') {
            screenRecorderRef.current.stop();
          }
          stopScreenRecording({ triggeredByUserStop: true });
        };
      }

    } catch (error) {
      console.error("Error starting screen recording:", error);
      setScreenShareError(error.code === "ENTIRE_SCREEN_REQUIRED"
        ? ENTIRE_SCREEN_REQUIRED_MESSAGE
        : "Screen sharing permission was not granted.");
      throw error;
    }
  };

  const stopScreenRecording = async ({ triggeredByUserStop = false } = {}) => {
    const recorder = screenRecorderRef.current;
    const screenStream = screenStreamRef.current;
    const micStream = micStreamRef.current;

    // Remove the onended handler to prevent re-entrant calls
    const videoTrack = screenStream?.getVideoTracks?.()[0];
    if (videoTrack) {
      videoTrack.onended = null;
    }

    // IMPORTANT: recorder.stop() is async — the browser fires ondataavailable with the
    // final buffered chunk, then fires onstop. We MUST wait for onstop before queueing
    // the merge call, otherwise the merge request (high-priority) races ahead of the
    // last chunk upload (low-priority) and the recording will be truncated.
    if (recorder && recorder.state === 'recording') {
      await new Promise((resolve) => {
        const originalOnStop = recorder.onstop;
        recorder.onstop = async () => {
          // Flush final partial chunk (delivered just before onstop)
          if (originalOnStop) {
            try { await originalOnStop(); } catch (e) {}
          }
          // Any chunk pushed by ondataavailable after our recorder.stop() call
          if (screenChunksRef.current.length > 0) {
            try { sendScreenChunkToBackend(); } catch (e) {}
          }
          resolve();
        };
        try {
          recorder.stop();
        } catch (error) {
          console.warn("[TestingSection] Error stopping screen recorder:", error);
          resolve();
        }
      });
    }

    // Stop all media tracks
    if (screenStream) {
      screenStream.getTracks().forEach(track => {
        try { track.stop(); } catch (e) {}
      });
    }

    if (micStream) {
      micStream.getTracks().forEach(track => {
        try { track.stop(); } catch (e) {}
      });
    }

    setIsScreenRecording(false);
    setScreenSharingActive(false);

    screenRecorderRef.current = null;
    screenStreamRef.current = null;
    micStreamRef.current = null;
    screenChunksRef.current = [];

    if (triggeredByUserStop) {
      setScreenShareStatus("Error");
      setScreenShareError("Screen sharing was stopped. Please restart.");
      setShowScreenShareStoppedPopup(true);
    } else {
      // Drain all queued low-priority chunk uploads first, THEN queue the merge.
      // Without this, queueHighPriorityRequest(merge) would run before the last
      // chunk uploads finish because the queue always processes high-priority first.
      requestQueue.drainLowPriorityQueue().then(() => {
        requestQueue.queueHighPriorityRequest(() =>
          fetch('/api/merge_screen_recording', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ test_id })
          })
        );
      });
    }
  };

  const handleScreenShareTest = async () => {
    if (IS_DESKTOP) {
      console.log("Desktop mode: skipping browser screen share test");
      setScreenShareStatus("Tested");
      return;
    }
  
    setScreenShareStatus("Testing");
    setScreenShareError("");
    
    // Clean up any previous failed attempts
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    if (screenRecorderRef.current && screenRecorderRef.current.state !== 'inactive') {
      try {
        screenRecorderRef.current.stop();
      } catch (e) {}
      screenRecorderRef.current = null;
    }
    screenChunksRef.current = [];
    setIsScreenRecording(false);
    setScreenSharingActive(false);
    
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        throw new Error("Screen sharing not supported in this browser");
      }

      await startScreenRecording();
      
      if (!screenStreamRef.current || !screenRecorderRef.current) {
        throw new Error("Screen recording failed to start");
      }
      
      if (screenRecorderRef.current.state !== 'recording') {
        throw new Error("Screen recording not in recording state");
      }
      
      setScreenShareStatus("Tested");
      setScreenShareError("");
      
      console.log("Screen share test successful");
      
    } catch (error) {
      console.error("Screen share access denied or failed:", error);
      setScreenShareStatus("Error");
      const message = error.code === "ENTIRE_SCREEN_REQUIRED"
        ? ENTIRE_SCREEN_REQUIRED_MESSAGE
        : error.message === "Screen sharing not supported in this browser"
          ? "Screen sharing is not supported in this browser. Please use a supported desktop browser such as Chrome."
          : "Screen sharing was not allowed. Please allow screen sharing and select Entire Screen to continue.";
      setScreenShareError(message);
      setShowScreenShareErrorPopup(true);
    }
  };

  // ==================== Navigation Handlers ====================

  const handleBackToDashboard = () => {
    navigate('/user-home');
  };

  const handleGuidelinesContinue = () => {
    setCurrentStep(2);
  };

  const handleSystemCheckBack = () => {
    setCurrentStep(1);
  };

  const handleSystemCheckContinue = () => {
    setCurrentStep(3);
  };

  const handleReadyBack = () => {
    setCurrentStep(2);
  };

  const handleStartInterview = async () => {
    setIsStartingInterview(true);
    
    console.log("Starting interview - Screen recording state:", {
      isScreenRecording,
      screenSharingActive,
      hasScreenStream: !!screenStreamRef.current,
      hasScreenRecorder: !!screenRecorderRef.current
    });

    // Start desktop proctoring if in desktop app
    if (IS_DESKTOP) {
      console.log("[Desktop] Starting proctoring for test:", test_id, testType);
      const proctoringResult = await startDesktopProctoring(test_id, testType);
      console.log("[Desktop] Proctoring result:", proctoringResult);

      if (proctoringResult?.warnings?.length > 0) {
        setProctoringWarnings(proctoringResult.warnings);
        proctoringResult.warnings.forEach(w => {
          console.warn("[Desktop] Proctoring warning:", w.message);
        });
      }
    }

    // Save screen recording state to global manager
    screenRecordingManager.setScreenRecordingState({
      screenStreamRef: screenStreamRef.current,
      screenRecorderRef: screenRecorderRef.current,
      screenChunksRef: screenChunksRef.current,
      micStreamRef: micStreamRef.current,
      isScreenRecording,
      screenSharingActive,
      screenShareError,
      testStarted: true
    });

    if (test_id && questions) {
      const destination = testType?.toLowerCase() === "c"
        ? `/icoding?test_id=${test_id}`
        : testType?.toLowerCase() === "qc"
        ? `/qcoding?test_id=${test_id}`
        : testType?.toLowerCase() === "apt"
        ? `/aptitude?test_id=${test_id}`
        : (testType?.toLowerCase() === "rs" || testType?.toLowerCase() === "ro" || testType?.toLowerCase() === "rpnb")
        ? `/real-time-questions?test_id=${test_id}`
        : `/questions?test_id=${test_id}`;
        
      navigate(destination, {
        state: {
          questions,
          resumeQuestions,
          testType,
          backendLanguage,
          isVeryHard,
          codingQuestions,
          proctoringWarnings,
          durationMinutes,
          'total_questions': questionsCount
        }
      });
    } else {
      navigate('/questions', {
        state: {
          questions,
          resumeQuestions,
          testType,
          backendLanguage,
          isVeryHard,
          codingQuestions,
          proctoringWarnings,
          'total_questions': questionsCount
        }
      });
    }
  };

  // ==================== Render ====================

  return (
    <div className={cn("bg-background py-4 px-4")}>
      <MediaDebugInfo />
      
      {/* Progress indicator */}
      <div className="max-w-4xl mx-auto mb-4">
        <SetupProgress currentStep={currentStep} />
      </div>

      {/* Step content */}
      <div className="max-w-4xl mx-auto pb-4">
        {currentStep === 1 && (
          <GuidelinesStep
            consentGiven={consentGiven}
            setConsentGiven={setConsentGiven}
            onBack={handleBackToDashboard}
            onContinue={handleGuidelinesContinue}
          />
        )}

        {currentStep === 2 && (
          <SystemCheckStep
            videoRef={videoRef}
            micStatus={micStatus}
            speakerStatus={speakerStatus}
            cameraStatus={cameraStatus}
            screenShareStatus={screenShareStatus}
            micMessage={micMessage}
            networkStatus={networkStatus}
            networkSpeed={networkSpeed}
            isDesktop={IS_DESKTOP}
            onMicTest={handleMicTest}
            onSpeakerTest={handleSpeakerTest}
            onCameraTest={handleCameraTest}
            onScreenShareTest={handleScreenShareTest}
            onBack={handleSystemCheckBack}
            onContinue={handleSystemCheckContinue}
            autoTestsComplete={!autoTestsRunning}
            testsStarted={testsStarted}
            onStartTests={runAllSystemChecks}
          />
        )}

        {currentStep === 3 && (
          <ReadyStep
            testType={testType}
            questionsCount={questionsCount}
            onBack={handleReadyBack}
            onStartInterview={handleStartInterview}
            isLoading={isStartingInterview}
            isReady={questionsLoaded} 
          />
        )}
      </div>

      {/* Screen Share Stopped Dialog */}
      <Dialog open={showScreenShareStoppedPopup} onOpenChange={setShowScreenShareStoppedPopup}>
        <DialogContent className={cn("sm:max-w-[500px]")}>
          <DialogHeader>
            <DialogTitle>Screen Sharing Stopped</DialogTitle>
            <DialogDescription>
              <div className={cn("space-y-4 mt-4")}>
                <div>
                  <Heading level="h3" className={cn("text-lg font-semibold mb-2")}>Screen share is required to continue</Heading>
                  <Text>Your browser reported that screen sharing was stopped. Please restart the screen share test to proceed with the interview.</Text>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className={cn("flex flex-col sm:flex-row gap-3")}>
            <Button
              onClick={() => {
                setShowScreenShareStoppedPopup(false);
                handleScreenShareTest();
              }}
              className={cn("w-full sm:w-auto")}
            >
              Restart Screen Share Test
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowScreenShareStoppedPopup(false)}
              className={cn("w-full sm:w-auto")}
            >
              Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Screen Share Error Dialog */}
      <Dialog open={showScreenShareErrorPopup} onOpenChange={setShowScreenShareErrorPopup}>
        <DialogContent className={cn("sm:max-w-[520px]")}>
          <DialogHeader>
            <DialogTitle className={cn("text-destructive font-bold")}>Error in Screen Sharing</DialogTitle>
            <DialogDescription>
              <Text className={cn("mt-4 text-destructive font-bold")}>
                {screenShareError || "Screen sharing failed. Please try again."}
              </Text>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className={cn("flex flex-col sm:flex-row gap-3")}>
            <Button
              onClick={() => {
                setShowScreenShareErrorPopup(false);
                handleScreenShareTest();
              }}
              className={cn("w-full sm:w-auto")}
            >
              Try Screen Share Again
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowScreenShareErrorPopup(false)}
              className={cn("w-full sm:w-auto text-destructive border-destructive font-bold hover:bg-destructive/10")}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TestingSection;
